import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";

import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import Alerts from "../../components/Alerts/alerts";
import DataTable from 'react-data-table-component';
import fileDownload from "js-file-download";

import ComplaintsService from "./complaints.service";
import AuthService from "../Authentication/auth.service";

const ComplaintsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Dropdown options
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [categories, setCategories] = useState([]);

  // Modal states
  const [discussionModal, setDiscussionModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [discussionLoading, setDiscussionLoading] = useState(false);
  const [discussionComplaint, setDiscussionComplaint] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const commentsEndRef = React.useRef(null);

  // Status/Priority change modal
  const [statusPriorityModal, setStatusPriorityModal] = useState(false);
  const [selectedComplaintForUpdate, setSelectedComplaintForUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState('');

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (discussionModal || statusPriorityModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [discussionModal, statusPriorityModal]);

  useEffect(() => {
    if (discussionModal && commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [discussionModal, discussionComplaint?.comments?.length]);

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchStatuses();
    fetchPriorities();
    fetchCategories();
    fetchComplaints(1, pageLimit);
  }, [pageLimit]);

  const fetchStatuses = () => {
    ComplaintsService.getStatuses().then((response) => {
      if (response.data.status) {
        setStatuses(response.data.data);
      }
    });
  };

  const fetchPriorities = () => {
    ComplaintsService.getPriorities().then((response) => {
      if (response.data.status) {
        setPriorities(response.data.data);
      }
    });
  };

  const fetchCategories = () => {
    ComplaintsService.getCategories().then((response) => {
      if (response.data.status) {
        setCategories(response.data.data);
      }
    });
  };

  const fetchComplaints = (page, limit) => {
    var filter = {};

    if (statusFilter !== 'All') {
      filter.status = statusFilter;
    }
    if (priorityFilter !== 'All') {
      filter.priority = priorityFilter;
    }
    if (categoryFilter !== 'All') {
      filter.category = categoryFilter;
    }
    if (startDate && endDate) {
      filter.startDate = startDate;
      filter.endDate = endDate;
    }
    if (searchText) {
      filter.search = searchText;
    }

    var offset = (page - 1) * limit;
    if (offset < 0) offset = 0;

    var postData = {
      sort: { created_at: -1 },
      offset: offset,
      limit: limit,
      filter: filter
    };

    setLoading(true);
    ComplaintsService.getComplaints(postData).then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else if (response.data.status) {
        setComplaints(response.data.data);
        setTotalRows(response.data.count?.total || 0);
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to fetch complaints");
      }
    }, (error) => {
      setLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      Alerts.swalErrorAlert(errContent || "Server Error, Please try again later.");
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchComplaints(page, pageLimit);
  };

  function buildComplaintsExportFilter() {
    var filter = {};
    if (statusFilter !== "All") filter.status = statusFilter;
    if (priorityFilter !== "All") filter.priority = priorityFilter;
    if (categoryFilter !== "All") filter.category = categoryFilter;
    if (startDate && endDate) {
      filter.startDate = startDate;
      filter.endDate = endDate;
    }
    if (searchText) filter.search = searchText;
    return filter;
  }

  function exportComplaintsToExcel() {
    const payload = {
      sort: { created_at: -1 },
      limit: 12000,
      filter: buildComplaintsExportFilter(),
    };

    setLoading(true);
    ComplaintsService.exportComplaints(payload).then(
      (response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
          return;
        }
        const pack = response.data && response.data.data;
        if (
          !response.data ||
          !response.data.status ||
          !pack ||
          typeof pack.csv !== "string"
        ) {
          Alerts.swalErrorAlert(
            (response.data && response.data.message) ||
              t("Export failed. Please try again.")
          );
          return;
        }
        fileDownload(
          pack.csv,
          pack.filename || `complaints-export-${new Date().toISOString().slice(0, 10)}.csv`,
          "text/csv;charset=utf-8;"
        );
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
      }
    );
  }

  const handlePerRowsChange = (newLimit, page) => {
    setPageLimit(newLimit);
  };

  const handleSearch = () => {
    setResetPaginationToggle(!resetPaginationToggle);
    fetchComplaints(1, pageLimit);
  };

  const resetFilters = () => {
    setSearchText('');
    setStatusFilter('All');
    setPriorityFilter('All');
    setCategoryFilter('All');
    setStartDate('');
    setEndDate('');
    setResetPaginationToggle(!resetPaginationToggle);
    fetchComplaints(1, pageLimit);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'New': 'primary',
      'In Progress': 'warning',
      'Pending Customer Response': 'info',
      'Resolved': 'success',
      'Closed': 'secondary'
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Low': 'success',
      'Medium': 'warning',
      'High': 'danger',
      'Critical': 'dark'
    };
    return <Badge color={colors[priority] || 'secondary'}>{priority}</Badge>;
  };

  const openStatusPriorityModal = (complaint) => {
    setSelectedComplaintForUpdate(complaint);
    setNewStatus(complaint.status);
    setNewPriority(complaint.priority);
    setStatusPriorityModal(true);
  };

  const closeStatusPriorityModal = () => {
    setStatusPriorityModal(false);
    setSelectedComplaintForUpdate(null);
    setNewStatus('');
    setNewPriority('');
  };

  const handleUpdateStatusPriority = () => {
    if (!selectedComplaintForUpdate) return;

    setLoading(true);
    const updateData = {
      complaint_id: selectedComplaintForUpdate._id
    };

    if (newStatus !== selectedComplaintForUpdate.status) {
      updateData.status = newStatus;
    }
    if (newPriority !== selectedComplaintForUpdate.priority) {
      updateData.priority = newPriority;
    }

    ComplaintsService.updateComplaint(updateData).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Complaint updated successfully");
        closeStatusPriorityModal();
        fetchComplaints(currentPage, pageLimit);
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to update complaint");
      }
    }, (error) => {
      setLoading(false);
      Alerts.swalErrorAlert("Failed to update complaint");
    });
  };

  const openDiscussionModal = (complaint) => {
    setSelectedComplaint(complaint);
    setDiscussionModal(true);
    setDiscussionLoading(true);
    setReplyMessage('');
    setReplyFiles([]);

    // Use optimized discussion API instead of full complaint
    ComplaintsService.getComplaintDiscussion(complaint._id).then((response) => {
      setDiscussionLoading(false);
      if (response.data.status && response.data.data.length > 0) {
        // Merge discussion data with selected complaint data for display
        setDiscussionComplaint({
          ...complaint,
          ...response.data.data[0]
        });
      } else {
        Alerts.swalErrorAlert("Failed to load discussion");
        setDiscussionModal(false);
      }
    }, (error) => {
      setDiscussionLoading(false);
      Alerts.swalErrorAlert("Failed to load discussion");
      setDiscussionModal(false);
    });
  };

  const closeDiscussionModal = () => {
    setDiscussionModal(false);
    setSelectedComplaint(null);
    setDiscussionComplaint(null);
    setReplyMessage('');
    setReplyFiles([]);
  };

  // Convert files to Base64 (similar to profile_image pattern)
  const convertFilesToBase64 = (files) => {
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            resolve({
              filename: file.name,
              data: reader.result // Base64 data URL: "data:mime/type;base64,..."
            });
          };
          reader.onerror = (error) => reject(error);
        });
      })
    );
  };

  const handleAddReplyInModal = async () => {
    if (!replyMessage.trim()) {
      Alerts.swalErrorAlert("Please enter a message");
      return;
    }

    setDiscussionLoading(true);
    
    // Convert files to Base64 before sending
    let base64Attachments = [];
    if (replyFiles && replyFiles.length > 0) {
      try {
        base64Attachments = await convertFilesToBase64(replyFiles);
      } catch (error) {
        setDiscussionLoading(false);
        Alerts.swalErrorAlert("Failed to convert files to Base64");
        return;
      }
    }

    const formData = {
      complaint_id: selectedComplaint._id,
      message: replyMessage,
      status: discussionComplaint.status,
      attachments: base64Attachments
    };

    ComplaintsService.addReply(formData).then((response) => {
      setDiscussionLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert("Reply added successfully");
        setReplyMessage('');
        setReplyFiles([]);
        // Refresh discussion data using optimized API
        ComplaintsService.getComplaintDiscussion(selectedComplaint._id).then((response) => {
          if (response.data.status && response.data.data.length > 0) {
            // Merge discussion data with existing complaint data
            setDiscussionComplaint({
              ...discussionComplaint,
              ...response.data.data[0]
            });
          }
        });
        // Refresh list
        fetchComplaints(currentPage, pageLimit);
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to add reply");
      }
    }, (error) => {
      setDiscussionLoading(false);
      Alerts.swalErrorAlert("Failed to add reply");
    });
  };

  function confirmDeleteComplaint(_id, referenceNumber) {
    const refLabel = referenceNumber ? ` (${referenceNumber})` : '';
    const msg = t("Once deleted, you will not be able to recover this complaint!") + refLabel;
    Alerts.swalConfirmDeleteAlert(msg).then((res) => {
      if (res === true) {
        sendDeleteComplaint(_id);
      }
    });
  }

  function sendDeleteComplaint(id) {
    setLoading(true);
    ComplaintsService.deleteComplaint(id).then((response) => {
      setLoading(false);
      if (response.data.status) {
        Alerts.swalSuccessAlert(response.data.message || t("Complaint deleted successfully."));
        fetchComplaints(currentPage, pageLimit);
      } else if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else {
        Alerts.swalErrorAlert(response.data.message || t("Failed to delete complaint."));
      }
    }, () => {
      setLoading(false);
      Alerts.swalErrorAlert(t("Failed to delete complaint."));
    });
  }

  const columns = React.useMemo(() => [
    {
      name: 'Reference Number',
      selector: row => row.reference_number,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Customer',
      selector: row => {
        // Use company_name from populated customer_details, fallback to customer_name for backward compatibility
        if (row.customer_details && row.customer_details.company_name) {
          return row.customer_details.company_name;
        }
        return row.customer_name || 'N/A';
      },
      sortable: true,
      width: '200px'
    },
    {
      name: 'Subject',
      selector: row => row.subject,
      sortable: true,
      wrap: true
    },
    {
      name: 'Category',
      selector: row => row.category,
      sortable: true,
      width: '180px'
    },
    {
      name: 'Priority',
      cell: row => getPriorityBadge(row.priority),
      sortable: true,
      width: '120px'
    },
    {
      name: 'Status',
      cell: row => getStatusBadge(row.status),
      sortable: true,
      width: '150px'
    },
    {
      name: 'Created At',
      selector: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
      sortable: true,
      width: '120px'
    },
    {
      name: 'Actions',
      cell: row => (
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            color="info"
            size="sm"
            id={`discussion-${row._id}`}
            // onClick={() => {}}
            onClick={() => openDiscussionModal(row)}
            style={{ minWidth: 'auto', padding: '4px 8px' }}
          >
            <i className="pe-7s-chat"></i>
          </Button>
          <UncontrolledTooltip placement="top" target={`discussion-${row._id}`}>
            View Discussion
          </UncontrolledTooltip>

          <Button
            color="warning"
            size="sm"
            id={`status-priority-${row._id}`}
            onClick={() => openStatusPriorityModal(row)}
            style={{ minWidth: 'auto', padding: '4px 8px' }}
          >
            <i className="pe-7s-settings"></i>
          </Button>
          <UncontrolledTooltip placement="top" target={`status-priority-${row._id}`}>
            Change Status & Priority
          </UncontrolledTooltip>

          <Button
            color="primary"
            size="sm"
            onClick={() => navigate(`/view-complaint?id=${row._id}`)}
            id={`view-${row._id}`}
            style={{ minWidth: 'auto', padding: '4px 8px' }}
          >
            <i className="pe-7s-look"></i>
          </Button>
          <UncontrolledTooltip placement="top" target={`view-${row._id}`}>
            View Details
          </UncontrolledTooltip>

          <Button
            color="danger"
            size="sm"
            id={`delete-${row._id}`}
            onClick={() => confirmDeleteComplaint(row._id, row.reference_number)}
            style={{ minWidth: 'auto', padding: '4px 8px' }}
          >
            <i className="pe-7s-trash"></i>
          </Button>
          <UncontrolledTooltip placement="top" target={`delete-${row._id}`}>
            Delete Complaint
          </UncontrolledTooltip>
        </div>
      ),
      width: '220px',
      minWidth: '220px'
    }
  ], [statuses, priorities, navigate]);

  // Filter component for subHeader
  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          type="text"
          placeholder="Search by reference or customer"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: '200px' }}
        />
        <Input
          type="select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: '150px' }}
        >
          <option value="All">All Statuses</option>
          {statuses.map((status, index) => (
            <option key={index} value={status.value}>{status.name}</option>
          ))}
        </Input>
        <Input
          type="select"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          style={{ width: '150px' }}
        >
          <option value="All">All Priorities</option>
          {priorities.map((priority, index) => (
            <option key={index} value={priority.value}>{priority.name}</option>
          ))}
        </Input>
        <Input
          type="select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ width: '150px' }}
        >
          <option value="All">All Categories</option>
          {categories.map((category, index) => (
            <option key={index} value={category.value}>{category.name}</option>
          ))}
        </Input>
        <Input
          type="date"
          placeholder="Start Date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={{ width: '150px' }}
        />
        <Input
          type="date"
          placeholder="End Date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={{ width: '150px' }}
        />
        <Button color="primary" size="sm" onClick={handleSearch}>
          Search
        </Button>
        <Button color="secondary" size="sm" onClick={resetFilters}>
          Reset
        </Button>
      </div>
    );
  }, [searchText, statusFilter, priorityFilter, categoryFilter, startDate, endDate, statuses, priorities, categories, resetPaginationToggle]);

  return (
    <Fragment>
      <style>{`
        body.modal-open {
          overflow: hidden !important;
          padding-right: 0 !important;
        }
        .modal {
          overflow-y: auto !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .modal-dialog {
          margin: auto !important;
          display: flex !important;
          align-items: center !important;
          min-height: calc(100% - 3.5rem);
          box-shadow: none;
          width: 100%;
        }
        .modal-content {
          border: none;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          margin: auto;
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal.show .modal-dialog {
          transform: none !important;
        }
      `}</style>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <TransitionGroup>
            <CSSTransition component="div" classNames="TabsAnimation" appear={true}
              timeout={1500} enter={false} exit={false}>
              <div>
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-note2 icon-gradient bg-happy-green" />
                      </div>
                      <div>
                        {t('Complaints Management')}
                        <div className="page-title-subheading">
                          {t('View all complaints, create, reply and manage complaint tickets.')}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <button
                        type="button"
                        onClick={exportComplaintsToExcel}
                        className="btn btn-outline-success btn-hover-shine me-2"
                      >
                        {t("Export to Excel")}
                      </button>
                      <button type="button" onClick={() => navigate(-1)} className="btn btn-outline-success btn-hover-shine me-3">
                        {t('Back')}
                      </button>
                    </div>
                  </div>
                </div>
                <Row>
                  <Col md="12">
                    <Card className="main-card mb-3">
                      <CardBody>
                        <LoadingOverlay tag="div" active={loading}
                          styles={{ overlay: (base) => ({ ...base }) }}
                          spinner={<DNALoader />}>
                          <Col md="12" style={{ float: "left", top: '4px', position: "absolute", zIndex: "1" }}>
                            {subHeaderComponentMemo}
                          </Col>
                          <DataTable
                            columns={columns}
                            data={complaints}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                            subHeader
                            subHeaderComponent={
                              <a href="/add-complaint" className="AddData " style={{ zIndex: '99' }}>
                                <button type="button" className="btn btn-custom-color btn-hover-shine me-3 btn">
                                  {t('Create Complaint')}
                                </button>
                              </a>
                            }
                            highlightOnHover
                            pointerOnHover
                            selectableRowDisabled={true}
                            persistTableHead
                          />
                        </LoadingOverlay>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>

      {/* Discussion Modal */}
      <Modal
        isOpen={discussionModal}
        toggle={closeDiscussionModal}
        size="lg"
        centered
        backdrop={true}
        keyboard={true}
        zIndex={1050}
      >
        <ModalHeader toggle={closeDiscussionModal} style={{paddingLeft: '28px', paddingRight: '28px'}}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', }}>
            <i className="pe-7s-chat" style={{ fontSize: '20px', color: '#007bff' }}></i>
            Discussion - {selectedComplaint?.reference_number || ''}
          </div>

          {discussionComplaint && (
            <div style={{ marginBottom: '0', padding: '0 28px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                {discussionComplaint.subject || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#6c757d' }}>
                {(discussionComplaint.customer_details?.company_name || 'N/A')}  -  {discussionComplaint.customer_details?.email || 'N/A'}
              </div>
            </div>
          )}
        </ModalHeader>

        <ModalBody style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden', padding: '1.5rem' }}>
          <LoadingOverlay tag="div" active={discussionLoading}
            styles={{ overlay: (base) => ({ ...base }) }}
            spinner={<DNALoader />}
          >

            {discussionComplaint && (
              <div>

                {/* COMMENTS LIST */}
                {discussionComplaint.comments && discussionComplaint.comments.length > 0 ? (
                  <div style={{ marginBottom: '20px' }}>
                    {discussionComplaint.comments.map((comment, index) => (
                      <div key={index} style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#007bff',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            marginRight: '10px'
                          }}>
                            {(comment.created_by_name || 'A').charAt(0).toUpperCase()}
                          </div>

                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>
                              {comment.created_by_name || 'Admin'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>
                              {new Date(comment.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        <div style={{ marginLeft: '42px', fontSize: '14px', color: '#495057', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                          {comment.message}
                        </div>

                        {comment.attachments && comment.attachments.length > 0 && (
                          <div style={{ marginLeft: '42px', marginTop: '8px' }}>
                            {comment.attachments.map((attachment, attIndex) => {
                              // Handle Base64 format (for AddCustomerReply attachments)
                              const isBase64 = attachment && attachment.data && attachment.data.startsWith('data:');
                              const attachmentUrl = isBase64 
                                ? attachment.data 
                                : (() => {
                                    const baseUrlForFiles = process.env.REACT_APP_LOGINURL?.replace('/api/', '/') || 'http://localhost:3005/';
                                    const attachmentPath = typeof attachment === 'string' ? attachment : (attachment.path || attachment);
                                    return attachmentPath.startsWith('http')
                                      ? attachmentPath
                                      : `${baseUrlForFiles}${attachmentPath}`;
                                  })();
                              const attachmentFilename = isBase64
                                ? (attachment.filename || 'attachment')
                                : (typeof attachment === 'string'
                                  ? (attachment.split('/').pop() || 'attachment')
                                  : (attachment.filename || (attachment.path ? attachment.path.split('/').pop() : 'attachment')));

                              // Handle click for Base64 attachments (open in new window properly)
                              const handleAttachmentClick = (e) => {
                                if (isBase64) {
                                  e.preventDefault();
                                  // Convert Base64 to blob URL for more reliable opening
                                  try {
                                    const base64Data = attachment.data.split(',')[1];
                                    const mimeType = attachment.data.split(',')[0].match(/:(.*?);/)[1];
                                    const byteCharacters = atob(base64Data);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], { type: mimeType });
                                    const blobUrl = URL.createObjectURL(blob);
                                    const newWindow = window.open(blobUrl, '_blank');
                                    if (newWindow) {
                                      // Clean up blob URL after a delay
                                      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                                    }
                                  } catch (error) {
                                    console.error('Error opening Base64 attachment:', error);
                                    // Fallback: try direct open
                                    window.open(attachment.data, '_blank');
                                  }
                                }
                                // For non-Base64, let default link behavior work
                              };

                              return (
                                <a
                                  key={attIndex}
                                  href={isBase64 ? '#' : attachmentUrl}
                                  target={isBase64 ? undefined : "_blank"}
                                  rel={isBase64 ? undefined : "noopener noreferrer"}
                                  onClick={handleAttachmentClick}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 8px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    textDecoration: 'none',
                                    color: '#007bff',
                                    fontSize: '12px',
                                    marginRight: '8px',
                                    border: '1px solid #dee2e6',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <i className="pe-7s-file me-1"></i>
                                  {attachmentFilename}
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>
                    <i className="pe-7s-chat" style={{ fontSize: '40px', opacity: 0.3, display: 'block', marginBottom: '10px' }}></i>
                    No messages yet. Start the conversation below.
                  </div>
                )}

                {/* ===================== COMPACT REPLY SECTION ===================== */}

                <div style={{ borderTop: '2px solid #e9ecef', paddingTop: '12px' }}>

                  {/* ATTACHMENT CHIPS */}
                  {replyFiles && replyFiles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                      {replyFiles.map((file, index) => (
                        <div key={index}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#f1f3f5',
                            fontSize: '12px',
                            border: '1px solid #dee2e6',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          📎 {file.name}
                          <span
                            onClick={() => setReplyFiles(prev => prev.filter((_, i) => i !== index))}
                            style={{ cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            ×
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* INPUT ROW */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '6px 10px'
                    }}
                  >
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      rows={1}
                      placeholder="Type your reply…"
                      style={{
                        flex: 1,
                        resize: 'none',
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px'
                      }}
                    />

                    <input
                      id="fileInputReply"
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => setReplyFiles(Array.from(e.target.files))}
                    />

                    <label
                      htmlFor="fileInputReply"
                      style={{ cursor: 'pointer', fontSize: '18px', padding: '4px 6px' }}
                      title="Attach files"
                    >
                      📎
                    </label>

                    <button
                      onClick={handleAddReplyInModal}
                      disabled={!replyMessage.trim()}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      Send
                    </button>
                  </div>
                </div>

                <div ref={commentsEndRef} />
              </div>
            )}
          </LoadingOverlay>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={closeDiscussionModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>


      {/* Status & Priority Update Modal */}
      <Modal
        isOpen={statusPriorityModal}
        toggle={closeStatusPriorityModal}
        size="md"
        centered
        backdrop={true}
        keyboard={true}
        zIndex={1050}
      >
        <ModalHeader toggle={closeStatusPriorityModal}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="pe-7s-settings" style={{ fontSize: '20px', color: '#ffc107' }}></i>
            Update Status & Priority
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden', padding: '1.5rem' }}>
          {selectedComplaintForUpdate && (
            <div>
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  {selectedComplaintForUpdate.reference_number}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  {selectedComplaintForUpdate.subject}
                </div>
              </div>

              <FormGroup>
                <Label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="pe-7s-flag" style={{ color: '#007bff' }}></i>
                  Status
                </Label>
                <Input
                  type="select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                >
                  {statuses.map((status, index) => (
                    <option key={index} value={status.value}>{status.name}</option>
                  ))}
                </Input>
              </FormGroup>

              <FormGroup>
                <Label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="pe-7s-star" style={{ color: '#ffc107' }}></i>
                  Priority
                </Label>
                <Input
                  type="select"
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value)}
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                >
                  {priorities.map((priority, index) => (
                    <option key={index} value={priority.value}>{priority.name}</option>
                  ))}
                </Input>
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeStatusPriorityModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdateStatusPriority}>
            <i className="pe-7s-check me-2"></i>
            Update
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ComplaintsList;
